import * as React from 'react'
import { View, Text } from 'react-native'
import Icon from '@components/icon/Icons'
import styled from '@theme/styled-components'
import Theme from '@theme/Theme'

import { scoreToRate } from '@screens/controle/form/utils'
import { ControleQualiteNature } from '@api/enum'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'

import useReducer from '@store/useReducer'
import * as SiteStore from '@store/site/site'
import * as SocieteStore from '@store/societe/societe'
import * as MetierStore from '@store/metier/metier'
import { findManifestation } from '@screens/manifestations/manifestationsUtils'

interface Props {
  controle: ControleQualiteResponse
}

const ControleCard = ({ controle }: Props) => {
  const i18n = useI18n()
  const isPhone = useBreakpoint()[1] === 'phone'

  const nature = scoreToRate(controle.tauxConformite)

  const metiers = useReducer(MetierStore.store, (s) => s.metiers)
  const metier = metiers.find((m) => m.id === controle.metierId)

  const manifestationsStore = useReducer(SiteStore.store, (s) => s.manifestations)
  const manifestation = findManifestation(manifestationsStore, controle.manifestationId)

  const societes = useReducer(SocieteStore.store, (s) => s.services)
  const societe = societes.find((s) => s.id === controle.societeId)

  const infos = [
    i18n.t(`components.controleCard.type.${controle.type}`),
    societe ? societe.nameSociete : i18n.t('components.controleCard.unknownPresta'),
  ]
  const allInfos =
    controle.nature === ControleQualiteNature.PRESTATION
      ? [...infos, manifestation ? manifestation.libelle : i18n.t('components.controleCard.unknownManifestation')]
      : infos

  const renderScore = () => {
    if (isPhone) {
      return (
        <>
          <Score>{controle.tauxConformite}%</Score>
          <Icon name="arrow" size={24} color={Theme.colors.primary} />
        </>
      )
    }
    return (
      <ScoreContainer>
        <LightInfo>{i18n.t('components.controleCard.conformity')}</LightInfo>
        <Score>{controle.tauxConformite}%</Score>
      </ScoreContainer>
    )
  }

  return (
    <MainContainer>
      <LeftContainer>
        <TagContainer color={nature.color}>
          <Tag>{i18n.t(`components.controleCard.nature.${nature.key}`)}</Tag>
        </TagContainer>
        <Metier numberOfLines={1} ellipsizeMode="tail">
          {metier?.libelle || i18n.t('components.controleCard.unknownMetier')}
        </Metier>
        <Info numberOfLines={2} ellipsizeMode="tail">
          {allInfos.join(' - ')}
        </Info>
        <LightInfo>{i18n.t('components.controleCard.date', { date: new Date(controle.dateValidation) })}</LightInfo>
      </LeftContainer>
      {renderScore()}
    </MainContainer>
  )
}

export default ControleCard

const MainContainer = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && `width: 360px;`}
  ${(props) => props.theme.constants.shadow};
  flex-direction: row;
  align-items: center;
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.background};
  overflow: hidden;
  padding: 16px;
`

const LeftContainer = styled(View)`
  flex: 1;
`

const TagContainer = styled(View)<{ color: string }>`
  margin-bottom: 4px;
  align-self: flex-start;
  background-color: ${(props) => props.color};
  border-radius: 11px;
  padding: 4px 8px;
`

const Tag = styled(Text)`
  ${(props) => props.theme.fonts.Caption2};
`

const Score = styled(Text)`
  margin: 0 8px;
  ${(props) => props.theme.fonts.Headline};
  color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.secondaryText : props.theme.colors.primaryText};
`

const Metier = styled(Text)`
  margin-bottom: 8px;
  ${(props) => props.theme.fonts.Headline};
`

const Info = styled(Text)`
  margin-bottom: 8px;
  height: 26px;
  ${(props) => props.theme.fonts.Caption2};
`

const LightInfo = styled(Text)`
  ${(props) => props.theme.fonts.Caption2};
  color: ${(props) => props.theme.colors.secondaryText};
`

const ScoreContainer = styled(View)`
  padding: 8px 14px;
  align-items: center;
  border-radius: 6px;
  background-color: ${(props) => props.theme.colors.backgroundSecondary};
  margin-left: 8px;
`
